import type { ReactNode } from "react";
import clsx from "clsx";
import type { SubjectUpdate } from "@/pages/support/ticketConversation";
import { fmtRelative } from "@/pages/support/utils";

export function TicketBrief({
  original,
  originalAt,
  reporterLabel,
  current,
  attachments,
  showOriginal = true,
  className,
}: {
  original: string;
  originalAt?: string | null;
  reporterLabel: string;
  current: SubjectUpdate | null;
  attachments?: ReactNode;
  showOriginal?: boolean;
  className?: string;
}) {
  if (!showOriginal && !current) return null;

  const originalText = original.trim() || "(Keine Beschreibung)";

  return (
    <section className={clsx("overflow-hidden rounded-ios border border-separator bg-card", className)}>
      <div className="border-b border-separator px-4 py-3">
        <h3 className="text-sm font-semibold text-label">Worum es geht</h3>
        <p className="mt-0.5 text-xs text-label-tertiary">
          {current && showOriginal
            ? "Die letzte Rückmeldung steht zuerst. Das ursprüngliche Anliegen bleibt darunter."
            : current
              ? "Das steht aktuell an, noch bevor der Verlauf beginnt."
              : "Ursprüngliche Meldung – bleibt hier sichtbar, ohne den Verlauf zu öffnen."}
        </p>
      </div>

      {current ? (
        <div className={clsx("bg-accent/[0.07] px-4 py-4", showOriginal && "border-b border-accent/20")}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="rounded-full bg-accent px-2.5 py-0.5 text-[11px] font-semibold text-label-on-accent">
              {current.label}
            </span>
            <span className="text-xs text-label-secondary">
              {current.actor}
              {current.at ? ` · ${fmtRelative(current.at)}` : ""}
            </span>
          </div>
          <p className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed text-label">{current.text}</p>
        </div>
      ) : null}

      {showOriginal ? (
        <div className={clsx("px-4 py-4", current && "bg-fill-quaternary/10")}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">
              Ursprüngliches Anliegen
            </p>
            <p className="text-xs text-label-tertiary">
              {reporterLabel}
              {originalAt ? ` · ${fmtRelative(originalAt)}` : ""}
            </p>
          </div>
          <p
            className={clsx(
              "mt-2 whitespace-pre-wrap leading-relaxed",
              current ? "text-sm text-label-secondary" : "text-[15px] text-label",
            )}
          >
            {originalText}
          </p>
          {attachments ? <div className="mt-3">{attachments}</div> : null}
        </div>
      ) : null}
    </section>
  );
}
